<template>
  <div class="global-search" v-if="isOpen" @keydown.esc.stop="close">
    <div class="global-search__overlay" @click="close"></div>
    <div class="global-search__container">
      <div class="global-search__header">
        <input
          ref="searchInput"
          type="text"
          class="global-search__input text-input"
          placeholder="Search files... (use tag:name to filter by tag)"
          v-model="searchQuery"
          @input="onSearch"
          @keydown.enter="goToResult"
          @keydown.down.prevent="selectNext"
          @keydown.up.prevent="selectPrev"
        >
        <button class="global-search__close-button button" @click="close">
          <icon-close></icon-close>
        </button>
      </div>
      <div class="global-search__results" v-if="searchQuery.length >= 2 || searchQuery.toLowerCase().includes('tag:')">
        <div v-if="isSearching" class="global-search__loading">
          Searching...
        </div>
        <div v-else-if="results.length === 0" class="global-search__empty">
          No results found
        </div>
        <div
          v-else
          v-for="(result, index) in results"
          :key="result.fileId"
          class="global-search__result"
          :class="{ 'global-search__result--selected': index === selectedIndex }"
          @click="openResult(result)"
          @mouseenter="selectedIndex = index"
        >
          <div class="global-search__result-title">{{ result.fileName }}</div>
          <div class="global-search__result-preview" v-html="result.preview"></div>
          <div class="global-search__result-count">{{ result.matchCount }} match{{ result.matchCount > 1 ? 'es' : '' }}</div>
        </div>
      </div>
      <div class="global-search__hint" v-else>
        Type at least 2 characters to search, or use <code>tag:name</code> to filter by tags
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import store from '../store';
import localDbSvc from '../services/localDbSvc';

export default {
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      results: [],
      selectedIndex: 0,
      isSearching: false,
      searchTimeout: null,
    };
  },
  computed: {
    ...mapState('file', ['itemsById']),
    files() {
      return store.getters['file/items'].filter(file => 
        file.parentId !== 'trash' && file.id
      );
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleGlobalKeydown);
    window.addEventListener('open-global-search', this.open);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    window.removeEventListener('open-global-search', this.open);
  },
  methods: {
    handleGlobalKeydown(e) {
      // Cmd/Ctrl + Shift + F to open global search
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'f') {
        e.preventDefault();
        this.open();
      }
    },
    open() {
      this.isOpen = true;
      this.searchQuery = '';
      this.results = [];
      this.selectedIndex = 0;
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },
    close() {
      this.isOpen = false;
      this.searchQuery = '';
      this.results = [];
    },
    onSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      const hasTagFilter = this.searchQuery.toLowerCase().includes('tag:');
      if (this.searchQuery.length < 2 && !hasTagFilter) {
        this.results = [];
        return;
      }

      this.isSearching = true;
      this.searchTimeout = setTimeout(() => {
        this.performSearch();
      }, 300);
    },
    async performSearch() {
      const searchResults = [];
      
      // Parse tag filters from query
      // Supports: tag:name (AND multiple), tag:name|tag:other (OR with |)
      let textQuery = this.searchQuery;
      const andTagFilters = [];
      const orTagFilters = [];
      
      // Check for OR syntax first (tag:a | tag:b)
      const orPattern = /tag:(\S+)\s*\|\s*tag:(\S+)/gi;
      let orMatch;
      while ((orMatch = orPattern.exec(this.searchQuery)) !== null) {
        orTagFilters.push(orMatch[1].toLowerCase(), orMatch[2].toLowerCase());
        textQuery = textQuery.replace(orMatch[0], '').trim();
      }
      
      // Then parse remaining AND tags
      const tagPattern = /tag:(\S+)/gi;
      let match;
      while ((match = tagPattern.exec(textQuery)) !== null) {
        andTagFilters.push(match[1].toLowerCase());
        textQuery = textQuery.replace(match[0], '').trim();
      }
      
      const query = textQuery.toLowerCase();
      const isTagOnlySearch = (andTagFilters.length > 0 || orTagFilters.length > 0) && !query;

      for (const file of this.files) {
        try {
          // Check tag filters
          if (andTagFilters.length > 0 || orTagFilters.length > 0) {
            const fileTags = (file.tags || []).map(t => t.toLowerCase());
            
            // AND logic: must have ALL tags
            if (andTagFilters.length > 0) {
              const hasAllTags = andTagFilters.every(tag => 
                fileTags.some(ft => ft.includes(tag))
              );
              if (!hasAllTags) continue;
            }
            
            // OR logic: must have at least ONE of the OR tags
            if (orTagFilters.length > 0) {
              const hasAnyTag = orTagFilters.some(tag => 
                fileTags.some(ft => ft.includes(tag))
              );
              if (!hasAnyTag) continue;
            }
          }
          
          // If only searching by tags, add all matching files
          if (isTagOnlySearch) {
            searchResults.push({
              fileId: file.id,
              fileName: file.name,
              preview: `<span class="tag-match">Tags: ${(file.tags || []).join(', ')}</span>`,
              matchCount: 1,
              matchIndex: 0,
              tags: file.tags || [],
            });
            continue;
          }
          
          // Search in content
          if (query) {
            const content = await localDbSvc.loadItem(`${file.id}/content`);
            if (content && content.text) {
              const text = content.text;
              const lowerText = text.toLowerCase();
              const matchIndex = lowerText.indexOf(query);
              
              if (matchIndex !== -1) {
                // Count total matches
                let matchCount = 0;
                let pos = 0;
                while ((pos = lowerText.indexOf(query, pos)) !== -1) {
                  matchCount++;
                  pos += query.length;
                }

                // Create preview with highlighted match
                const start = Math.max(0, matchIndex - 40);
                const end = Math.min(text.length, matchIndex + query.length + 40);
                let preview = text.substring(start, end);
                
                // Add ellipsis if needed
                if (start > 0) preview = '...' + preview;
                if (end < text.length) preview = preview + '...';
                
                // Highlight the match
                const regex = new RegExp(`(${this.escapeRegex(textQuery)})`, 'gi');
                preview = preview.replace(regex, '<mark>$1</mark>');

                searchResults.push({
                  fileId: file.id,
                  fileName: file.name,
                  preview,
                  matchCount,
                  matchIndex,
                  tags: file.tags || [],
                });
              }
            }
          }
        } catch (e) {
          // File content not loaded, skip
        }
      }

      // Sort by match count (most matches first)
      searchResults.sort((a, b) => b.matchCount - a.matchCount);
      
      this.results = searchResults;
      this.selectedIndex = 0;
      this.isSearching = false;
    },
    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    selectNext() {
      if (this.results.length > 0) {
        this.selectedIndex = (this.selectedIndex + 1) % this.results.length;
      }
    },
    selectPrev() {
      if (this.results.length > 0) {
        this.selectedIndex = (this.selectedIndex - 1 + this.results.length) % this.results.length;
      }
    },
    goToResult() {
      if (this.results.length > 0 && this.results[this.selectedIndex]) {
        this.openResult(this.results[this.selectedIndex]);
      }
    },
    openResult(result) {
      store.commit('file/setCurrentId', result.fileId);
      this.close();
      
      // TODO: Could also highlight/scroll to the match in the editor
    },
  },
};
</script>

<style lang="scss">
@import '../styles/variables.scss';

.global-search {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  display: flex;
  justify-content: center;
  padding-top: 80px;
}

.global-search__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
}

.global-search__container {
  position: relative;
  width: 600px;
  max-width: 90%;
  max-height: 70vh;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .app--dark & {
    background: #2d2d2d;
    color: rgba(255, 255, 255, 0.87);
  }

  .app--sepia & {
    background: #f4ecd8;
    color: #5b4636;
  }

  .app--nord & {
    background: #3b4252;
    color: #d8dee9;
  }

  .app--solarized-light & {
    background: #fdf6e3;
    color: #657b83;
  }

  .app--solarized-dark & {
    background: #073642;
    color: #93a1a1;
  }

  .app--dracula & {
    background: #21222c;
    color: #f8f8f2;
  }

  .app--monokai & {
    background: #1e1f1c;
    color: #f8f8f2;
  }
}

.global-search__header {
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  .app--dark & {
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }
}

.global-search__input {
  flex: 1;
  font-size: 16px;
  padding: 8px 12px;
  border: none;
  outline: none;
  background: transparent;
  color: inherit;

  &::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }

  .app--dark &,
  .app--nord &,
  .app--solarized-dark &,
  .app--dracula &,
  .app--monokai & {
    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }
}

.global-search__close-button {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.5);
  background: transparent;

  .app--dark & {
    color: rgba(255, 255, 255, 0.5);
  }

  &:hover {
    color: rgba(0, 0, 0, 0.8);

    .app--dark & {
      color: rgba(255, 255, 255, 0.8);
    }
  }

  .icon {
    width: 18px;
    height: 18px;
  }
}

.global-search__results {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.global-search__result {
  padding: 10px 16px;
  cursor: pointer;
  border-left: 3px solid transparent;

  &:hover,
  &--selected {
    background: rgba(0, 0, 0, 0.05);
    border-left-color: $link-color;

    .app--dark & {
      background: rgba(255, 255, 255, 0.08);
    }
  }
}

.global-search__result-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  color: inherit;
}

.global-search__result-preview {
  font-size: 13px;
  color: inherit;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;

  mark {
    background: #ffeb3b;
    color: #000;
    padding: 0 2px;
    border-radius: 2px;
  }
}

.global-search__result-count {
  font-size: 11px;
  color: inherit;
  opacity: 0.5;
  margin-top: 4px;
}

.global-search__loading,
.global-search__empty,
.global-search__hint {
  padding: 20px;
  text-align: center;
  color: inherit;
  opacity: 0.6;
  font-size: 14px;
}
</style>
